import React, { useEffect, useCallback } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"

import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import { Typography, Divider, Flex, Button, message, Spin, Form, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import GuestsSelector from "./GuestSelector"
import DateSelector from "./DateSelector"
import DropdownSelector from "./DropDownSelector"
import { fetchExperienceBookingDetails, updateBookingSelections, resetBookingSelections } from "store/experiences"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import { applyPlatformFeeSetting } from "pagesV2/pro/Bookings/helpers/calculateBookingTotal"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"

const { Text } = Typography

const BookingDetailCard = ({ bookNow, trip, bookingButtonVisible, activeTab, bookingErrors, setBookingErrors, form, mode }) => {
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const location = useLocation()
  const bookingId = location.state?.b_uuid
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const isBookNow = location?.pathname?.includes("/experience_detail") || location?.pathname?.includes("/trips")
  const {
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)
  const userRole = useSelector((state) => state.user.role)

  const infoData = [
    { label: "Date", value: "Add Date" },
    { label: "Guests", value: "Add Guests" },
    { label: "Trip Length", value: "Add Trip Length" },
    { label: "Time", value: "Add Time" }
  ]

  const depositAmount = ((bookingSelections?.amount || 0) * (experienceDetail?.deposit_percentage || 0)) / 100
  const balanceAmount = (bookingSelections?.amount || 0) - depositAmount

  const calculateBookingTotal = useCallback(
    (counts, guideFeePercent, customFees = [], platformFeeSetting) => {
      let total = 0
      const breakdown = []

      const priceData = bookingSelections?.tripLength?.price || {}
      const { pricing_type, flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger, included_guests, max_guests } = priceData

      const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0)

      const totalCounts = counts?.main || {
        adults: 0,
        children: 0,
        infants: 0,
        passengers: 0
      }

      if (pricing_type === "charge_flat_rate") {
        const flatAmount = Number.parseFloat(flat_rate ?? 0)
        const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers // Usually passengers are extra.
        // Assuming passengers are separate.
        // Let's stick to Adults/Children/Infants for included logic

        const hasGuests = totalGuests > 0 || totalCounts.passengers > 0
        if (hasGuests) {
          total += flatAmount
          // Logic: Waterfall (Adults -> Children -> Infants)
          const guestsIncluded = included_guests ?? max_guests ?? 0

          let remainingCapacity = guestsIncluded

          // 1. Adults
          const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

          // 2. Children
          // Are children allowed?
          const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

          // 3. Infants
          const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

          const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
          const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
          const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

          breakdown.push({
            label: "Flat Rate",
            amount: numberToCurrency(flatAmount),
            pricingDetails: {
              pricing_type,
              included_guests: guestsIncluded,
              price_per_person,
              price_per_child,
              price_per_infant
            }
          })

          if (extraAdults > 0) {
            const lineTotal = extraAdults * getPrice(price_per_person)
            total += lineTotal
            breakdown.push({ label: `Extra Adult x ${extraAdults}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraChildren > 0) {
            const lineTotal = extraChildren * getPrice(price_per_child)
            total += lineTotal
            breakdown.push({ label: `Extra Child x ${extraChildren}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraInfants > 0) {
            const lineTotal = extraInfants * getPrice(price_per_infant)
            total += lineTotal
            breakdown.push({ label: `Extra Infant x ${extraInfants}`, amount: numberToCurrency(lineTotal) })
          }
          if (totalCounts.passengers > 0) {
            const lineTotal = totalCounts.passengers * getPrice(price_per_passenger)
            total += lineTotal
            breakdown.push({ label: `Passenger x ${totalCounts.passengers}`, amount: numberToCurrency(lineTotal) })
          }
        }
      } else {
        // Per Person Pricing
        const mapping = [
          { count: totalCounts.adults, label: "Adult", price: price_per_person },
          { count: totalCounts.children, label: "Child", price: price_per_child },
          { count: totalCounts.infants, label: "Infant", price: price_per_infant },
          { count: totalCounts.passengers, label: "Passenger", price: price_per_passenger }
        ]

        for (const { count, label, price } of mapping) {
          if (count > 0) {
            const lineTotal = count * getPrice(price)
            total += lineTotal
            breakdown.push({ label: `${label} x ${count}`, amount: numberToCurrency(lineTotal) })
          }
        }
      }

      // Save guest subtotal before fees
      const guestSubtotal = total

      // Custom fees - collected, not pushed individually
      const customFeeDetails = []
      let customFeesTotal = 0
      if (guestSubtotal > 0 && customFees?.length > 0) {
        for (const fee of customFees) {
          const feeAmount =
            fee.fee_type === "percentage"
              ? (guestSubtotal * Number(fee.price)) / 100
              : Number(fee.price)
          total += feeAmount
          customFeesTotal += feeAmount
          customFeeDetails.push({ label: fee.title, amount: numberToCurrency(feeAmount) })
        }
      }

      // Platform booking fee (apply split setting if configured)
      let taxAmount = 0
      if (guideFeePercent && guestSubtotal > 0) {
        const rawTaxAmount = (guestSubtotal * guideFeePercent) / 100
        taxAmount = applyPlatformFeeSetting(rawTaxAmount, platformFeeSetting)
        total += taxAmount
      }

      // Combined Tax & Fees line with info breakdown
      const combinedTaxTotal = customFeesTotal + taxAmount
      if (combinedTaxTotal > 0) {
        const infoItems = [
          ...customFeeDetails,
          ...(taxAmount > 0 ? [{ label: "Booking Fee", amount: numberToCurrency(taxAmount) }] : [])
        ]
        breakdown.push({
          label: "Tax & Fees",
          amount: numberToCurrency(combinedTaxTotal),
          info: infoItems.length > 1 ? infoItems : undefined
        })
      }

      return { total, breakdown }
    },
    [bookingSelections?.tripLength?.price]
  )

  useEffect(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts
    if (tripPricing && counts) {
      const { total, breakdown } = calculateBookingTotal(counts, trip?.guide_booking_fee_percentage, experienceDetail?.assigned_custom_fees, experienceDetail?.platform_fee_setting)
      dispatch(updateBookingSelections({ amount: total, breakdown }))
    }
  }, [bookingSelections?.tripLength, bookingSelections?.counts, calculateBookingTotal, dispatch, trip?.guide_booking_fee_percentage, experienceDetail?.assigned_custom_fees, experienceDetail?.platform_fee_setting])

  const handleDateChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ date: dayjs(value).format("YYYY-MM-DD"), timeSlot: null }))
      if (value && bookingSelections.tripLength?.key) {
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            bookingSelections?.tripLength?.key,
            dayjs(value).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: false }))
      }
    },
    [dispatch, bookingSelections.tripLength, id, mode, setBookingErrors]
  )

  const handleTripLengthChange = useCallback(
    (tripLength) => {
      // If counts already exist, keep them, else set default counts
      const hasCounts = Object.values(bookingSelections?.counts?.main ?? {}).some((v) => v > 0)
      dispatch(
        updateBookingSelections({
          tripLength,
          timeSlot: null,
          counts: hasCounts
            ? bookingSelections?.counts
            : {
              main: {
                adults: experienceBookingData?.bookingData?.min_guests,
                children: 0,
                infants: 0,
                passengers: 0
              },
              extra: {
                adults: 0,
                children: 0,
                infants: 0,
                passengers: 0
              }
            }
        })
      )
      if (tripLength?.key && bookingSelections?.date) {
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            tripLength?.key,
            dayjs(bookingSelections?.date).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, tripLength: false }))
      }
    },
    [dispatch, bookingSelections?.date, bookingSelections?.counts, mode, id, setBookingErrors, experienceBookingData?.bookingData?.min_guests]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ timeSlot: value }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, timeSlot: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      dispatch(updateBookingSelections({ counts: newCounts }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  useEffect(() => {
    if (bookNow) {
      if (id) {
        dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", bookingId, mode))
      }
      // Reset only on Experience Detail or Trips pages, not in the wizard routes
      if (isBookNow) {
        dispatch(resetBookingSelections())
      }
    }
  }, [bookNow, dispatch, id, bookingId, mode, isBookNow])
  useEffect(() => {
    const tripDurations = experienceBookingData?.bookingData?.trip_durations
    // If only one trip duration, auto-select it
    if (
      tripDurations?.length === 1 &&
      (!bookingSelections?.tripLength || bookingSelections?.tripLength?.value !== tripDurations?.[0]?.value)
    ) {
      handleTripLengthChange(tripDurations?.[0])
    }
    // Removed auto-clear of tripLength when more than one trip duration to preserve user selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.trip_durations])

  useEffect(() => {
    const timeSlots = experienceBookingData?.bookingData?.data
    if (timeSlots?.length === 1 && !bookingSelections?.timeSlot && !experienceBookingData?.loading) {
      handleTimeSlotChange(timeSlots[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.data, experienceBookingData?.loading, bookingSelections?.timeSlot])

  return (
    <Spin spinning={experienceBookingData?.loading}>
      <div className={`mt-15 ${styles.bookingCard}`}>
        <Flex gap={8} align="center">
          <Text style={{ fontSize: "18px", fontWeight: 600 }}>{`${bookNow ? trip?.title ?? "" : "From $" + trip?.price_per_person}`}</Text>
          <PricingDetailsTooltip tripPrices={trip?.trip_prices} />
        </Flex>
        <Flex vertical gap={8} className="py-xl">
          {bookNow === false ? (
            infoData?.map((data, index) => {
              return (
                <Flex justify="space-between" align="center" className={`${styles.cardDetailItems} p-s rounded-s`} key={index + 1}>
                  <Text>{data?.label}</Text>
                  <Text>{data?.value}</Text>
                </Flex>
              )
            })
          ) : (
            <>
              <DateSelector
                value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
                onChange={handleDateChange}
                activeTab={activeTab}
                error={bookingErrors?.date}
                trip={trip}
              />
              <DropdownSelector
                value={bookingSelections.tripLength?.value}
                onChange={handleTripLengthChange}
                label="Trip Length"
                options={
                  experienceBookingData?.bookingData?.trip_durations?.length ? experienceBookingData?.bookingData?.trip_durations : []
                }
                placeholder="Add Trip Length"
                activeTab={activeTab}
                error={bookingErrors?.tripLength}
              />
              <DropdownSelector
                placeholder="Add Time"
                value={bookingSelections?.timeSlot}
                onChange={handleTimeSlotChange}
                label="Time"
                activeTab={activeTab}
                error={bookingErrors?.timeSlot}
                options={experienceBookingData?.bookingData?.data?.length ? experienceBookingData?.bookingData?.data : []}
              />
              <GuestsSelector
                counts={bookingSelections?.counts}
                setCounts={handleGuestsChange}
                activeTab={activeTab}
                error={bookingErrors?.counts}
                setBookingErrors={setBookingErrors}
                maxGuests={experienceBookingData?.bookingData?.max_guests}
                minGuests={experienceBookingData?.bookingData?.min_guests}
              />
            </>
          )}
        </Flex>
        {bookNow && (
          <>
            <Flex vertical gap={4}>
              {bookingSelections?.breakdown?.map((item, index) => (
                <Flex key={index + 1} align="center" justify="space-between">
                  <Flex align="center" gap={4}>
                    <Text>{item?.label}</Text>
                    {item?.pricingDetails ? (
                      <FlatRatePriceTooltip {...item.pricingDetails} />
                    ) : (
                      item?.info && (
                        <Tooltip
                          title={
                            Array.isArray(item.info) ? (
                              <div>
                                {item.info.map(({ label, amount }, i) => (
                                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                                    <span>{label}</span>
                                    <span>{amount}</span>
                                  </div>
                                ))}
                              </div>
                            ) : item.info
                          }
                        >
                          <InfoCircleOutlined style={{ color: "var(--color-primary)" }} />
                        </Tooltip>
                      )
                    )}
                  </Flex>
                  <Text>{item?.amount}</Text>
                </Flex>
              ))}
            </Flex>
            <Divider />
            <Flex align="center" justify="space-between">
              <Text strong style={{ fontWeight: 600 }}>
                Total
              </Text>
              <Text style={{ fontWeight: 600 }}>{numberToCurrency(bookingSelections?.amount)}</Text>
            </Flex>
            {selectedPayment && selectedPayment !== "full" && (
              <>
                <Flex align="center" justify="space-between">
                  <Text strong style={{ fontWeight: 600 }}>
                    Pay Now (Deposit)
                  </Text>
                  <Text style={{ fontWeight: 600 }}>{numberToCurrency(depositAmount)}</Text>
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text>
                    {selectedPayment === "later"
                      ? `Balance Due on ${moment(bookingSelections?.date).subtract(1, "day").format("MMMM D, YYYY")}`
                      : `Balance Due on ${moment(bookingSelections?.date).format("MMMM D, YYYY")}`}
                  </Text>
                  <Text>{`$ ${Number.parseFloat(balanceAmount || 0).toFixed(2)}`}</Text>
                </Flex>
              </>
            )}
          </>
        )}
        {bookingButtonVisible && (
          <>
            <Button
              type="primary"
              disabled={!bookNow}
              className={styles.bookButton}
              onClick={() => {
                if (isBookNow) {
                  const { date, counts, tripLength, timeSlot } = bookingSelections

                  const errors = {
                    date: !date,
                    counts: !counts?.main || Object.values(counts?.main).every((v) => Number(v) <= 0),
                    tripLength: !tripLength,
                    timeSlot: !timeSlot
                  }

                  const hasErrors = Object.values(errors).some(Boolean)

                  if (hasErrors) {
                    setBookingErrors(errors)
                    message.error("Please complete all booking selections before proceeding.")
                    return
                  }
                  setBookingErrors({ date: false, guests: false, tripLength: false, timeSlot: false })
                  if (mode) {
                    const isClientUser = userRole === "user"
                    if (isClientUser) navigate(routes.clientBookingWizardRootPath({ id }))
                    else navigate({ pathname: oldRoutes.bookingWizardPath({ id }) })
                  } else {
                    navigate(routes.embeddedExperienceBooking({ uuid, id }))
                  }
                }
              }}
            >
              Book Now
            </Button>
            <Flex justify="center" style={{ marginTop: "10px" }}>
              <Text style={{ fontSize: "12px" }}>{t("webIntegration.payment_info")}</Text>
            </Flex>
          </>
        )}
      </div>
    </Spin>
  )
}

BookingDetailCard.propTypes = {
  bookNow: PropTypes.bool,
  form: PropTypes.object,
  trip: PropTypes.shape({
    min_price: PropTypes.number,
    title: PropTypes.string,
    guide_booking_fee_percentage: PropTypes.number,
    price_per_person: PropTypes.string || PropTypes.number
  }),
  bookingButtonVisible: PropTypes.bool,
  activeTab: PropTypes.number,
  bookingErrors: PropTypes?.object,
  setBookingErrors: PropTypes.func,
  mode: PropTypes.string
}

export default BookingDetailCard
